/**
 * Utility functions for mapping muscles to exercises in workout routines
 */

/**
 * Creates a mapping of muscle groups to exercises that target them
 * @param {Array} exercises - Array of exercise objects with name and muscles properties
 * @returns {Object} - Object mapping muscle names to arrays of exercise objects
 */
export const createMuscleToExerciseMapping = (exercises) => {
  const mapping = {};
  
  exercises.forEach(exercise => {
    if (exercise.muscles && Array.isArray(exercise.muscles)) {
      exercise.muscles.forEach(muscle => {
        // Normalize muscle name for consistent mapping
        const normalizedMuscle = muscle.toLowerCase().trim();
        
        if (!mapping[normalizedMuscle]) {
          mapping[normalizedMuscle] = [];
        }
        
        // Add exercise info to the muscle mapping
        mapping[normalizedMuscle].push({
          name: exercise.name,
          sets: exercise.sets,
          reps: exercise.reps,
          rest: exercise.rest,
          description: exercise.description
        });
      });
    }
  });
  
  return mapping;
};

/**
 * Gets exercises that target a specific muscle group
 * @param {string} muscleName - Name of the muscle group
 * @param {Object} muscleToExerciseMap - Mapping object from createMuscleToExerciseMapping
 * @returns {Array} - Array of exercises targeting the muscle
 */
export const getExercisesForMuscle = (muscleName, muscleToExerciseMap) => {
  const normalizedMuscle = muscleName.toLowerCase().trim();
  return muscleToExerciseMap[normalizedMuscle] || [];
};

/**
 * Formats exercise information for tooltip display
 * @param {Array} exercises - Array of exercise objects for the muscle
 * @returns {string} Formatted HTML string for tooltip
 */
export const formatExerciseTooltip = (exercises) => {
  if (!exercises || exercises.length === 0) {
    return 'No exercises found for this muscle';
  }

  return exercises.map(exercise => exercise.name).join(', ');
};

/**
 * Maps SVG muscle IDs to common muscle names for exercise lookup
 * @param {string} svgMuscleId - ID from the SVG muscle diagram
 * @returns {string} - Common muscle name used in exercise data
 */
export const mapSvgMuscleToExerciseMuscle = (svgMuscleId) => {
  const muscleMapping = {
    // Chest muscles
    'chest': 'chest',
    'pectorals': 'chest',
    'upper_chest': 'upper chest',
    'lower_chest': 'lower chest',
    
    // Back muscles
    'latissimus_dorsi': 'latissimus dorsi',
    'lats': 'latissimus dorsi',
    'rhomboids': 'rhomboids',
    'middle_traps': 'middle traps',
    'traps': 'middle traps',
    'lower_back': 'lower back',
    'rear_deltoids': 'rear deltoids',
    
    // Shoulder muscles
    'shoulders': 'shoulders',
    'deltoids': 'shoulders',
    'anterior_deltoids': 'anterior deltoids',
    'front_delts': 'anterior deltoids',
    
    // Arm muscles
    'biceps': 'biceps',
    'triceps': 'triceps',
    'forearms': 'forearms',
    
    // Leg muscles
    'quadriceps': 'quadriceps',
    'quads': 'quadriceps',
    'hamstrings': 'hamstrings',
    'glutes': 'glutes',
    'calves': 'calves',
    'hip_flexors': 'hip flexors',
    
    // Core
    'core': 'core',
    'abs': 'core',
    'abdominals': 'core'
  };
  
  const normalizedId = svgMuscleId.toLowerCase().replace(/[-_]/g, '_');
  return muscleMapping[normalizedId] || svgMuscleId;
};